import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from 'lib/firebase';

/**
 * Custom hook for fetching questions and suggestions from Firestore
 * @param {boolean} isAdmin - Whether the current user is an admin
 * @returns {Object} { questions, suggestions, loading }
 */
export const useQuestions = (isAdmin) => {
  const [questions, setQuestions] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    // Query for questions (non-suggested)
    // Note: We can't use orderBy('queueOrder') because it excludes docs where queueOrder is null
    // So we order by status and then sort in memory
    const questionsQuery = query(
      collection(db, 'weekly_questions'),
      where('status', '!=', 'suggested'),
      orderBy('status')
    );

    // Query for suggestions
    const suggestionsQuery = query(
      collection(db, 'weekly_questions'),
      where('status', '==', 'suggested'),
      orderBy('createdAt', 'desc')
    );

    // Real-time listener for questions
    const unsubscribeQuestions = onSnapshot(questionsQuery, (snapshot) => {
      const qs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setQuestions(qs);
      setLoading(false);
    });

    // Real-time listener for suggestions
    const unsubscribeSuggestions = onSnapshot(suggestionsQuery, (snapshot) => {
      const ss = snapshot.docs
        .map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        .sort((a, b) => {
          // Sort by total reaction count
          const countA = Object.values(a.reactions || {}).reduce((acc, arr) => acc + arr.length, 0);
          const countB = Object.values(b.reactions || {}).reduce((acc, arr) => acc + arr.length, 0);
          return countB - countA;
        });
      setSuggestions(ss);
      setLoading(false);
    });

    return () => {
      unsubscribeQuestions();
      unsubscribeSuggestions();
    };
  }, [isAdmin]);

  return { questions, suggestions, loading };
};
